import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { createLead } from "@/lib/api";
import RateDisclaimer from "./RateDisclaimer";

/**
 * "Compare 1000's of rates & deals" — lead-capture focused calculator inspired by mortgage42.
 * Generates illustrative monthly repayment cards across three sample rates, then invites
 * the visitor to get personalised live rates (creates a lead tagged source='rate_compare').
 */
const gbp = new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
});

function monthly(loan, rate, years) {
    if (loan <= 0 || years <= 0) return 0;
    const r = rate / 100 / 12;
    const n = years * 12;
    if (r === 0) return loan / n;
    return (loan * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
}

const SAMPLE_DEALS = [
    { term: "2-year fixed", rate: 4.29, tag: "Lowest rate" },
    { term: "5-year fixed", rate: 4.59, tag: "Most popular" },
    { term: "Tracker", rate: 4.89, tag: "Flexible" },
];

export default function RateCompare() {
    const [purpose, setPurpose] = useState("purchase"); // purchase | remortgage
    const [type, setType] = useState("residential"); // residential | btl
    const [propertyValue, setPropertyValue] = useState(350000);
    const [deposit, setDeposit] = useState(70000);
    const [years, setYears] = useState(25);

    const loanAmount = useMemo(
        () => Math.max(0, Number(propertyValue) - Number(deposit)),
        [propertyValue, deposit]
    );

    const [showLead, setShowLead] = useState(false);
    const [contact, setContact] = useState({ name: "", email: "", phone: "", postcode: "" });
    const [sending, setSending] = useState(false);
    const navigate = useNavigate();

    const ltv = useMemo(
        () => (propertyValue > 0 ? Math.min(100, (loanAmount / propertyValue) * 100) : 0),
        [propertyValue, loanAmount]
    );

    const deals = useMemo(
        () =>
            SAMPLE_DEALS.map((d) => ({
                ...d,
                monthly: monthly(loanAmount, d.rate, years),
            })),
        [loanAmount, years]
    );

    const submitLead = async (e) => {
        e.preventDefault();
        if (!contact.name || !contact.email || !contact.phone) {
            toast.error("Please share your name, email and phone number.");
            return;
        }
        setSending(true);
        try {
            await createLead({
                ...contact,
                source: "rate_compare",
                service:
                    type === "btl"
                        ? "Buy-to-let"
                        : purpose === "remortgage"
                            ? "Remortgage"
                            : "Residential purchase",
                goal: `${purpose}_${type}`,
                property_value: Number(propertyValue) || null,
                deposit: Number(deposit) || null,
                timeline: null,
                message: `Rate comparison enquiry:\n• Purpose: ${purpose}\n• Type: ${type}\n• Property value: ${gbp.format(propertyValue)}\n• Deposit: ${gbp.format(deposit)}\n• Loan amount: ${gbp.format(loanAmount)}\n• Term: ${years} years\n• LTV: ${ltv.toFixed(0)}%`,
            });
            toast.success("Sent — an adviser will reach out with live rates.");
            navigate("/thank-you");
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            data-testid="rate-compare-section"
            id="rate-compare-section"
            className="relative pt-2 pb-14 md:pt-4 md:pb-20 bg-brand-bg"
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
                <RateDisclaimer className="mb-8" />
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
                    <div className="md:col-span-7">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="h-2.5 w-2.5 rounded-full bg-brand-coral" />
                            <span className="text-xs uppercase tracking-[0.22em] text-brand-accent font-bold">
                                Instant comparison
                            </span>
                        </div>
                        <h2
                            data-testid="rate-compare-title"
                            className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl text-brand-primary leading-[1.05]"
                        >
                            Compare{" "}
                            <em className="italic font-light text-brand-accent">
                                1,000s of rates
                            </em>
                            <br />
                            &amp; deals.
                        </h2>
                        <p className="mt-6 text-lg text-brand-muted leading-relaxed max-w-xl">
                            Quickly benchmark your numbers against representative
                            market rates. Like what you see? We'll match you with
                            personalised live deals from 90+ lenders.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Left — inputs */}
                    <div className="lg:col-span-5 bg-brand-surface border border-brand-border rounded-[14px] p-7 md:p-9 shadow-[0_24px_60px_-30px_rgba(26,54,38,0.25)]">
                        <PillToggle
                            label="What's the mortgage for?"
                            options={[
                                { value: "purchase", label: "Purchase" },
                                { value: "remortgage", label: "Remortgage" },
                            ]}
                            value={purpose}
                            onChange={setPurpose}
                            testid="rc-purpose"
                        />
                        <div className="h-5" />
                        <PillToggle
                            label="What type of mortgage?"
                            options={[
                                { value: "residential", label: "Residential" },
                                { value: "btl", label: "Buy-to-Let" },
                            ]}
                            value={type}
                            onChange={setType}
                            testid="rc-type"
                        />
                        <div className="h-6" />
                        <NumberField
                            label="Property value"
                            value={propertyValue}
                            onChange={(v) => {
                                setPropertyValue(v);
                                if (deposit > v) setDeposit(v);
                            }}
                            testid="rc-property-value"
                        />
                        <div className="h-5" />
                        <NumberField
                            label="Your deposit"
                            value={deposit}
                            onChange={(v) =>
                                setDeposit(Math.min(Math.max(0, v), propertyValue))
                            }
                            testid="rc-deposit"
                        />
                        <div className="h-5" />
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-[11px] uppercase tracking-[0.18em] text-brand-muted font-bold">
                                    Mortgage term
                                </label>
                                <span className="font-serif-editorial text-xl text-brand-primary">
                                    {years} yrs
                                </span>
                            </div>
                            <input
                                type="range"
                                min={5}
                                max={40}
                                value={years}
                                onChange={(e) => setYears(Number(e.target.value))}
                                data-testid="rc-term"
                                className="w-full accent-brand-primary"
                            />
                        </div>
                        <div className="mt-6 flex items-center justify-between pt-5 border-t border-brand-border">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted font-bold">
                                    Loan-to-Value
                                </div>
                                <div className="font-serif-editorial text-3xl text-brand-primary">
                                    {ltv.toFixed(0)}%
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted font-bold">
                                    Amount to borrow
                                </div>
                                <div className="font-serif-editorial text-3xl text-brand-primary">
                                    {gbp.format(loanAmount)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right — results */}
                    <div className="lg:col-span-7 space-y-4">
                        {deals.map((d, i) => (
                            <motion.div
                                key={d.term}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: i * 0.06 }}
                                data-testid={`rate-deal-${i}`}
                                className="bg-brand-surface border border-brand-border rounded-[14px] p-6 md:p-8 flex items-center gap-6 shadow-[0_12px_30px_-20px_rgba(26,54,38,0.25)] hover:shadow-[0_22px_50px_-20px_rgba(26,54,38,0.35)] transition-shadow"
                            >
                                <div className="hidden sm:flex h-14 w-14 items-center justify-center rounded-full bg-brand-primary/8 text-brand-primary shrink-0">
                                    <Search size={20} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <span className="font-bold text-brand-primary">
                                            {d.term}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-coral bg-brand-coral/12 rounded-full px-3 py-1">
                                            {d.tag}
                                        </span>
                                    </div>
                                    <div className="mt-1 text-xs text-brand-muted">
                                        Representative rate · {d.rate.toFixed(2)}% ·{" "}
                                        {years}-yr term
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted font-bold">
                                        From
                                    </div>
                                    <div className="font-serif-editorial text-3xl md:text-4xl text-brand-primary leading-none">
                                        {gbp.format(d.monthly)}
                                    </div>
                                    <div className="text-xs text-brand-muted mt-1">
                                        /month
                                    </div>
                                </div>
                            </motion.div>
                        ))}

                        <div className="bg-brand-surface border border-brand-border shadow-[0_20px_45px_-15px_rgba(30,79,138,0.22)] rounded-[14px] p-6 md:p-8">
                            <div className="flex items-start gap-4 flex-wrap">
                                <div className="hidden sm:flex h-12 w-12 items-center justify-center rounded-full bg-brand-accent text-white shrink-0">
                                    <Sparkles size={18} />
                                </div>
                                <div className="flex-1 min-w-[220px]">
                                    <div className="font-serif-editorial text-2xl leading-tight text-brand-primary">
                                        Like what you see?
                                    </div>
                                    <div className="text-sm text-brand-muted mt-1">
                                        Get live, personalised rates from our whole
                                        panel of 90+ lenders.
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowLead((v) => !v)}
                                    data-testid="rc-reveal-form"
                                    className="bg-brand-primary hover:bg-brand-primaryHover text-white px-6 py-3 rounded-full text-sm font-bold inline-flex items-center gap-2 transition-colors"
                                >
                                    {showLead ? "Hide form" : "Get personalised rates"}
                                    <ArrowRight size={14} />
                                </button>
                            </div>

                            {showLead && (
                                <form
                                    onSubmit={submitLead}
                                    data-testid="rc-lead-form"
                                    className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4"
                                >
                                    <DarkField
                                        label="Full name"
                                        value={contact.name}
                                        onChange={(v) =>
                                            setContact({ ...contact, name: v })
                                        }
                                        testid="rc-lead-name"
                                        required
                                    />
                                    <DarkField
                                        label="Email"
                                        type="email"
                                        value={contact.email}
                                        onChange={(v) =>
                                            setContact({ ...contact, email: v })
                                        }
                                        testid="rc-lead-email"
                                        required
                                    />
                                    <DarkField
                                        label="Phone"
                                        value={contact.phone}
                                        onChange={(v) =>
                                            setContact({ ...contact, phone: v })
                                        }
                                        testid="rc-lead-phone"
                                        required
                                    />
                                    <DarkField
                                        label="Postcode"
                                        value={contact.postcode}
                                        onChange={(v) =>
                                            setContact({ ...contact, postcode: v.toUpperCase() })
                                        }
                                        testid="rc-lead-postcode"
                                    />
                                    <button
                                        type="submit"
                                        disabled={sending}
                                        data-testid="rc-lead-submit"
                                        className="md:col-span-2 mt-2 bg-brand-primary text-white py-3.5 rounded-full text-sm font-bold hover:bg-brand-primaryHover transition-colors disabled:opacity-60"
                                    >
                                        {sending ? "Sending…" : "Send my details"}
                                    </button>
                                    <p className="md:col-span-2 text-[11px] text-brand-muted leading-relaxed">
                                        By submitting your details, you confirm you have read and understood our{" "}
                                        <a href="/privacy" className="underline text-brand-primary hover:text-brand-accent">
                                            Privacy Policy
                                        </a>
                                        .
                                    </p>
                                </form>
                            )}
                        </div>

                        <p className="text-[11px] text-brand-muted leading-relaxed">
                            Representative rates illustrated for comparison only. Not
                            a personal recommendation. Your home may be repossessed
                            if you do not keep up repayments on your mortgage.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function PillToggle({ label, options, value, onChange, testid }) {
    return (
        <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-brand-muted font-bold block mb-2">
                {label}
            </label>
            <div
                data-testid={testid}
                className="flex p-1 bg-brand-surfaceAlt rounded-full"
            >
                {options.map((o) => (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => onChange(o.value)}
                        data-testid={`${testid}-${o.value}`}
                        className={`flex-1 px-4 py-2.5 rounded-full text-sm font-bold transition-colors ${
                            value === o.value
                                ? "bg-brand-primary text-brand-bg"
                                : "text-brand-muted hover:text-brand-primary"
                        }`}
                    >
                        {o.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

function NumberField({ label, value, onChange, testid }) {
    return (
        <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-brand-muted font-bold">
                {label}
            </label>
            <div className="mt-1.5 flex items-center border-b border-brand-border focus-within:border-brand-primary">
                <span className="text-brand-muted mr-2">£</span>
                <input
                    type="number"
                    value={value}
                    onChange={(e) => onChange(Number(e.target.value) || 0)}
                    data-testid={testid}
                    className="flex-1 bg-transparent py-3 text-lg font-semibold text-brand-primary outline-none"
                />
            </div>
        </div>
    );
}

function DarkField({ label, value, onChange, type = "text", testid, required }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-brand-muted font-bold">
                {label}
                {required && <span className="text-brand-accent"> *</span>}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                required={required}
                className="mt-1.5 w-full bg-transparent border-b border-brand-border py-2.5 text-base text-brand-primary placeholder-brand-muted focus:border-brand-primary outline-none transition-colors"
            />
        </div>
    );
}
